import React from "react";

import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, TextField, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useForm } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { graphql } from "@/gql";
import { displayError } from "@/utils/errors";
import Spacer from "@components/layout/Spacer";
import { useSnackBar } from "@hooks/useSnackBar";

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(2, 4),
    width: "80%",
    background: theme.palette.common.white,
  },
}));

const StyledButton = withStyles(Button, (theme) => ({
  root: {
    alignSelf: "center",
    padding: theme.spacing(2, 6),
    minWidth: 103,
    minHeight: 45,
  },
}));

const validationSchema = yup.object().shape({
  oldPassword: yup
    .string()
    .label("Old Password")
    .required("Please enter your old password"),
  newPassword: yup
    .string()
    .label("New Password")
    .required("Please enter your new password")
    .min(12, "Password must contain a minimum of 12 characters."),
  confirmNewPassword: yup
    .string()
    .required()
    .oneOf(
      [yup.ref("newPassword")],
      "The password you entered does not match the confirmation password"
    ),
});

type FormValues = {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

const changePasswordMutation = graphql(`
  mutation ChangePassword($oldPassword: String!, $newPassword: String!) {
    changeAccountPassword(
      input: { oldPassword: $oldPassword, newPassword: $newPassword }
    ) {
      account {
        id
      }
      errors {
        fullMessage
      }
    }
  }
`);

const ChangePassword: React.FC = () => {
  const { classes: styles } = useStyles();
  const [SimpleSnackBar, snackBarHelper] = useSnackBar();
  const [changePassword] = useMutation(changePasswordMutation);
  const initialValues: FormValues = {
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const result = await changePassword({
        variables: {
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        },
      });
      snackBarHelper.open("Your changes has been saved.");

      if (result.data?.changeAccountPassword?.errors?.length) {
        result.data.changeAccountPassword.errors.map((e) => {
          snackBarHelper.open(e.fullMessage);
        });
      }
    } catch (e) {
      snackBarHelper.open(
        displayError(
          e,
          "An error occured while changing your password. Please try again later."
        )
      );
    }
  };

  return (
    <div className={styles.root}>
      <Typography variant="h5" fontWeight="bold">
        Change password
      </Typography>
      <Box marginBottom={4} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="flex"
          flexDirection="column"
          marginBottom="24px"
          width="50%"
        >
          <Typography>Old Password</Typography>
          <Spacer y={2} />
          <TextField
            error={!!errors.oldPassword}
            fullWidth
            helperText={errors.oldPassword?.message}
            placeholder="Enter old password"
            size="small"
            type="password"
            {...register("oldPassword")}
          />
          <Box marginBottom={2} />
          <Typography>New Password</Typography>
          <Spacer y={2} />
          <TextField
            error={!!errors.newPassword?.message}
            fullWidth
            placeholder="Enter new password"
            size="small"
            type="password"
            {...register("newPassword")}
          />
          <Box marginBottom={2} />
          <Typography>Confirm Password</Typography>
          <Spacer y={2} />
          <TextField
            error={!!errors.confirmNewPassword?.message}
            fullWidth
            helperText={errors.confirmNewPassword?.message ?? " "}
            placeholder="Enter confirm password"
            size="small"
            type="password"
            {...register("confirmNewPassword")}
          />
          <Box marginBottom={5} />

          <StyledButton disabled={isSubmitting} size="small" type="submit">
            {isSubmitting ? <CircularProgress size={20} /> : "Send"}
          </StyledButton>
        </Box>
      </form>
      <SimpleSnackBar />
    </div>
  );
};

export default ChangePassword;
